import { esSeccionAutomatica, type SeccionDePlantilla, TONOS_NOTA } from "@gestiondoc/shared";
import { NodeViewContent, type NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import { createContext, useContext, useEffect, useState } from "react";
import { type Dibujo, dibujarFlujograma } from "./mermaid.ts";
import { BLOQUES_NUEVOS } from "./nodos.ts";

/**
 * Las vistas React de los bloques propios. Lo que se dibuja acá es para editar; cómo se ve en
 * papel lo decide `HojaISO` (vista previa y PDF). Las dos comparten el JSON, no el HTML.
 */

export type ContextoDelEditor = {
  tipoCodigo: string;
  plantilla: SeccionDePlantilla[];
  /** Claves de las secciones obligatorias que todavía están vacías. */
  faltantes: string[];
  editable: boolean;
};

export const ContextoEditor = createContext<ContextoDelEditor>({
  tipoCodigo: "",
  plantilla: [],
  faltantes: [],
  editable: true,
});

/** Título numerado de la sección, su ayuda, y el aviso de "falta" si es obligatoria y está vacía. */
export function VistaSeccion({ node, editor, getPos }: NodeViewProps) {
  const ctx = useContext(ContextoEditor);
  const clave = (node.attrs.clave as string | null) ?? null;
  const definicion = ctx.plantilla.find((s) => s.clave === clave);
  const pos = getPos();
  const numero = pos === undefined ? 0 : editor.state.doc.resolve(pos).index() + 1;
  const automatica = esSeccionAutomatica(ctx.tipoCodigo, clave);
  const falta = clave !== null && ctx.faltantes.includes(clave);
  return (
    <NodeViewWrapper as="section" className="ed-seccion" data-clave={clave ?? undefined}>
      <header className="ed-seccion-cabecera" contentEditable={false}>
        <h2 className="ed-seccion-titulo">
          <span className="ed-seccion-n">{numero}.</span> {String(node.attrs.titulo)}
          {definicion?.obligatoria && !automatica && (
            <span className={`ed-etiqueta ${falta ? "ed-etiqueta-falta" : "ed-etiqueta-ok"}`}>
              {falta ? "Falta" : "Obligatoria"}
            </span>
          )}
        </h2>
        {definicion?.ayuda && <p className="ed-seccion-ayuda">{definicion.ayuda}</p>}
      </header>
      {automatica ? (
        <div className="ed-automatica" contentEditable={false}>
          La llena el sistema con el historial de revisiones. Se ve en la vista previa y en el PDF;
          no se edita.
        </div>
      ) : (
        <NodeViewContent as="div" className="ed-seccion-cuerpo" />
      )}
    </NodeViewWrapper>
  );
}

export function VistaPasos({ editor, getPos, node }: NodeViewProps) {
  const { editable } = useContext(ContextoEditor);
  function agregarPaso() {
    const pos = getPos();
    if (pos === undefined) return;
    // Al final del bloque: justo antes de su cierre.
    editor
      .chain()
      .focus()
      .insertContentAt(pos + node.nodeSize - 1, BLOQUES_NUEVOS.paso)
      .run();
  }
  return (
    <NodeViewWrapper className="ed-pasos">
      <div className="ed-pasos-cabecera" contentEditable={false}>
        <span className="ed-paso-n">#</span>
        <span>Paso</span>
        <span>Responsable</span>
      </div>
      <NodeViewContent as="div" className="ed-pasos-lista" />
      {editable && (
        <div className="ed-pasos-pie" contentEditable={false}>
          <button type="button" className="ed-boton-chico" onClick={agregarPaso}>
            + Agregar paso
          </button>
        </div>
      )}
    </NodeViewWrapper>
  );
}

export function VistaPaso({ node, updateAttributes, deleteNode, editor, getPos }: NodeViewProps) {
  const { editable } = useContext(ContextoEditor);
  const pos = getPos();
  const numero = pos === undefined ? 0 : editor.state.doc.resolve(pos).index() + 1;
  return (
    <NodeViewWrapper className="ed-paso">
      <span className="ed-paso-n" contentEditable={false}>
        {numero}
      </span>
      <NodeViewContent as="div" className="ed-paso-texto" />
      <div className="ed-paso-quien" contentEditable={false}>
        <input
          type="text"
          value={String(node.attrs.responsable)}
          placeholder="Quién lo hace"
          maxLength={120}
          disabled={!editable}
          onChange={(e) => updateAttributes({ responsable: e.target.value })}
          className="ed-entrada"
        />
        {editable && (
          <button
            type="button"
            className="ed-boton-quitar"
            title="Quitar este paso"
            onClick={() => deleteNode()}
          >
            ×
          </button>
        )}
      </div>
    </NodeViewWrapper>
  );
}

export function VistaNota({ node, updateAttributes }: NodeViewProps) {
  const { editable } = useContext(ContextoEditor);
  const tono = String(node.attrs.tono);
  return (
    <NodeViewWrapper className="ed-nota" data-tono={tono}>
      <div className="ed-nota-cabecera" contentEditable={false}>
        <select
          value={tono}
          disabled={!editable}
          onChange={(e) => updateAttributes({ tono: e.target.value })}
          className="ed-entrada ed-entrada-chica"
        >
          {TONOS_NOTA.map((t) => (
            <option key={t} value={t}>
              {t === "nota" ? "Nota" : "Advertencia"}
            </option>
          ))}
        </select>
      </div>
      <NodeViewContent as="div" className="ed-nota-texto" />
    </NodeViewWrapper>
  );
}

const ANCHOS = [40, 60, 80, 100] as const;

export function VistaImagen({ node, updateAttributes, deleteNode, selected }: NodeViewProps) {
  const { editable } = useContext(ContextoEditor);
  const archivoId = String(node.attrs.archivoId);
  const ancho = Number(node.attrs.ancho);
  return (
    <NodeViewWrapper className={`ed-imagen ${selected ? "ed-seleccionado" : ""}`}>
      <img
        src={`/archivos/${archivoId}`}
        alt={String(node.attrs.alt)}
        style={{ width: `${ancho}%` }}
        draggable={false}
      />
      <div className="ed-imagen-pie" contentEditable={false}>
        <input
          type="text"
          value={String(node.attrs.alt)}
          placeholder="Pie de la imagen (opcional)"
          maxLength={300}
          disabled={!editable}
          onChange={(e) => updateAttributes({ alt: e.target.value })}
          className="ed-entrada"
        />
        <select
          value={ancho}
          disabled={!editable}
          onChange={(e) => updateAttributes({ ancho: Number(e.target.value) })}
          className="ed-entrada ed-entrada-chica"
          title="Ancho respecto del cuerpo"
        >
          {ANCHOS.map((a) => (
            <option key={a} value={a}>
              {a} %
            </option>
          ))}
        </select>
        {editable && (
          <button
            type="button"
            className="ed-boton-quitar"
            title="Quitar la imagen"
            onClick={() => deleteNode()}
          >
            ×
          </button>
        )}
      </div>
    </NodeViewWrapper>
  );
}

export function VistaFlujograma({ node, updateAttributes, deleteNode, selected }: NodeViewProps) {
  const { editable } = useContext(ContextoEditor);
  const codigo = String(node.attrs.codigo);
  const id = String(node.attrs.id ?? "");
  const [dibujo, setDibujo] = useState<Dibujo | null>(null);
  // Se dibuja medio segundo después de la última tecla, no en cada una.
  useEffect(() => {
    let vigente = true;
    const t = setTimeout(() => {
      dibujarFlujograma(id || "nuevo", codigo).then((d) => {
        if (vigente) setDibujo(d);
      });
    }, 500);
    return () => {
      vigente = false;
      clearTimeout(t);
    };
  }, [id, codigo]);
  return (
    <NodeViewWrapper className={`ed-flujograma ${selected ? "ed-seleccionado" : ""}`}>
      <div className="ed-flujograma-cabecera" contentEditable={false}>
        <span className="ed-flujograma-titulo">Flujograma</span>
        <span className="ed-flujograma-ayuda">
          Se escribe en Mermaid y se dibuja igual en la vista previa y en el PDF.
        </span>
        {editable && (
          <button
            type="button"
            className="ed-boton-quitar"
            title="Quitar el flujograma"
            onClick={() => deleteNode()}
          >
            ×
          </button>
        )}
      </div>
      <div className="ed-flujograma-cuerpo" contentEditable={false}>
        <textarea
          value={codigo}
          disabled={!editable}
          rows={8}
          spellCheck={false}
          onChange={(e) => updateAttributes({ codigo: e.target.value })}
          className="ed-entrada ed-codigo"
        />
        <div className="ed-flujograma-dibujo">
          {dibujo === null ? (
            <span className="ed-flujograma-espera">Dibujando…</span>
          ) : dibujo.ok ? (
            <div
              // biome-ignore lint/security/noDangerouslySetInnerHtml: SVG de Mermaid en modo estricto
              dangerouslySetInnerHTML={{ __html: dibujo.svg }}
            />
          ) : (
            <span className="ed-flujograma-error">{dibujo.error}</span>
          )}
        </div>
      </div>
    </NodeViewWrapper>
  );
}

type Area = { nombre: string; puntos: string[] };

/**
 * Lista de verificación (fase 3b): cada área con su nombre y sus puntos, un punto por línea.
 * En la hoja sale como cuadrícula en blanco; las hojas llenas la marcan.
 */
export function VistaListaDeVerificacion({
  node,
  updateAttributes,
  deleteNode,
  selected,
}: NodeViewProps) {
  const { editable } = useContext(ContextoEditor);
  const areas = (node.attrs.areas as Area[]) ?? [];
  const poner = (nuevas: Area[]) => updateAttributes({ areas: nuevas });
  const cambiar = (i: number, cambio: Partial<Area>) =>
    poner(areas.map((a, j) => (j === i ? { ...a, ...cambio } : a)));
  return (
    <NodeViewWrapper className={`ed-verificacion ${selected ? "ed-seleccionado" : ""}`}>
      <div className="ed-flujograma-cabecera" contentEditable={false}>
        <span className="ed-flujograma-titulo">Lista de verificación</span>
        <span className="ed-flujograma-ayuda">
          Áreas y puntos a revisar. Cada hoja llena marca N/A, Aceptable o No aceptable.
        </span>
        {editable && (
          <button
            type="button"
            className="ed-boton-quitar"
            title="Quitar la lista"
            onClick={() => deleteNode()}
          >
            ×
          </button>
        )}
      </div>
      <div className="ed-verificacion-areas" contentEditable={false}>
        {areas.map((a, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: las áreas no tienen id; su posición es su identidad
          <div key={`area-${i}-${areas.length}`} className="ed-verificacion-area">
            <div className="ed-verificacion-fila">
              <input
                type="text"
                value={a.nombre}
                placeholder="Nombre del área"
                maxLength={120}
                disabled={!editable}
                onChange={(e) => cambiar(i, { nombre: e.target.value })}
                className="ed-entrada ed-verificacion-nombre"
              />
              {editable && areas.length > 1 && (
                <button
                  type="button"
                  className="ed-boton-quitar"
                  title="Quitar el área"
                  onClick={() => poner(areas.filter((_, j) => j !== i))}
                >
                  ×
                </button>
              )}
            </div>
            <textarea
              value={a.puntos.join("\n")}
              placeholder="Un punto por línea"
              rows={Math.max(3, a.puntos.length + 1)}
              disabled={!editable}
              onChange={(e) => cambiar(i, { puntos: e.target.value.split("\n") })}
              onBlur={(e) =>
                cambiar(i, {
                  puntos: e.target.value
                    .split("\n")
                    .map((x) => x.trim())
                    .filter((x) => x.length > 0),
                })
              }
              className="ed-entrada ed-verificacion-puntos"
            />
          </div>
        ))}
        {editable && (
          <button
            type="button"
            className="ed-boton-chico"
            onClick={() => poner([...areas, { nombre: "", puntos: [] }])}
          >
            + Agregar área
          </button>
        )}
      </div>
    </NodeViewWrapper>
  );
}
